import DOMPurify from "dompurify";
import { Eye, FileJson, FileText, RefreshCcwIcon, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from "react-bootstrap";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import APICall from "../../APICalls/APICall";
import PageSizeSelect from "../../components/PageSizeSelect";
import { useTheme } from "../../Context/ThemeContext";
import "./Conversation.css";

const parseUTCDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  if (!dateStr.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr + "Z");
  }
  return new Date(dateStr);
};

// Small pill showing who handled a conversation: Chatbot | AI Agent | Human Agent.
const HANDLED_BY = {
  ai_agent:    { label: "AI Agent",     bg: "rgba(139,92,246,0.16)",  color: "#8b5cf6" },
  human_agent: { label: "Human Agent",  bg: "rgba(239,68,68,0.15)",   color: "#ef4444" },
  chatbot:     { label: "Chatbot",      bg: "rgba(59,130,246,0.14)",  color: "#3b82f6" },
};
const HandledByBadge = ({ value }) => {
  const s = HANDLED_BY[value] || HANDLED_BY.chatbot;
  return (
    <span className="badge" style={{ backgroundColor: s.bg, color: s.color, fontWeight: 500 }}>
      {s.label}
    </span>
  );
};

const ConversationManager = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const selectStyles = {
    control: (base) => ({ ...base, border: "none", boxShadow: "none", backgroundColor: "transparent" }),
    container: (base) => ({ ...base, border: "none" }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#0e1d38" : "#ffffff",
      border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
      boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.1)",
      zIndex: 9999,
    }),
    menuList: (base) => ({ ...base, backgroundColor: "transparent", padding: "4px" }),
    option: (base, state) => ({
      ...base,
      borderRadius: "6px",
      backgroundColor: state.isSelected
        ? (isDark ? "rgba(232,113,10,0.25)" : "rgba(232,113,10,0.12)")
        : state.isFocused
          ? (isDark ? "rgba(255,255,255,0.06)" : "rgba(232,113,10,0.06)")
          : "transparent",
      color: isDark ? "#ffffff" : "#0f172a",
      cursor: "pointer",
    }),
    multiValue: (base) => ({ ...base, backgroundColor: isDark ? "rgba(232,113,10,0.2)" : "rgba(232,113,10,0.12)", borderRadius: "6px" }),
    multiValueLabel: (base) => ({ ...base, color: isDark ? "#ffffff" : "#0f172a" }),
    multiValueRemove: (base) => ({ ...base, color: isDark ? "rgba(255,255,255,0.6)" : "#475569", borderRadius: "0 6px 6px 0", ":hover": { backgroundColor: "rgba(232,113,10,0.3)", color: isDark ? "#ffffff" : "#0f172a" } }),
    placeholder: (base) => ({ ...base, color: isDark ? "rgba(255,255,255,0.35)" : "#94a3b8" }),
    singleValue: (base) => ({ ...base, color: isDark ? "#ffffff" : "#0f172a" }),
    input: (base) => ({ ...base, color: isDark ? "#ffffff" : "#0f172a" }),
    dropdownIndicator: (base) => ({ ...base, color: isDark ? "rgba(255,255,255,0.35)" : "#94a3b8" }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }),
    noOptionsMessage: (base) => ({ ...base, color: isDark ? "rgba(255,255,255,0.45)" : "#64748b" }),
  };

  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");


  const [statusFilter, setStatusFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState(conversations);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);


  useEffect(() => {
    fetchConversations();
    const id = setInterval(fetchConversations, 30_000);
    return () => clearInterval(id);
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const data = await APICall.getT('/conversation/conversations');
      const sessions = Array.isArray(data) ? data : (data.sessions || []);
      const normalized = sessions.map(mapSessionToConversation);
      setConversations(normalized);
      setFilteredConversations(normalized);
      // Deep-link: auto-open a session passed via ?session=<session_key>
      // (used by the Form Submissions "View Conversation" button).
      const sessionParam = searchParams.get("session");
      if (sessionParam) {
        const match = normalized.find((c) => c.id === sessionParam);
        if (match) { setSelectedConversation(match); setShowChatModal(true); }
      }
    } catch (err) {
      setToast({ variant: "danger", msg: "Failed to load conversations. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const openChat = (conv) => {
    setSelectedConversation(conv);
    setShowChatModal(true);
  };

  const mapSessionToConversation = (session) => {
    const msgs = session.conversations || [];

    const firstUserMsg =
      msgs.find(m => m.sender === "user")?.message_text ||
      msgs[0]?.message_text ||
      "";

    const lastMsg = msgs[msgs.length - 1];

    const lastBotWithIntent = [...msgs]
      .reverse()
      .find(m => m.intent_detected);

    return {
      id: session.session_key,
      sessionId: session.session_id,             // numeric PK used by escalation API
      convoId: lastMsg?.id || null,              // last message id for conversation_id
      user: session.user_name || "Anonymous",
      email: session.user_email || "",
      isGuest: session.is_guest ?? true,
      avatar: (session.user_name?.[0] || "U").toUpperCase(),

      firstMessage: firstUserMsg,

      startDate: session.started_at?.split("T")[0],
      endDate: session.ended_at?.split("T")[0] || null,

      lastTimestamp: lastMsg
        ? parseUTCDate(lastMsg.created_at)?.toLocaleString()
        : "",

      intent: lastBotWithIntent?.intent_label || lastBotWithIntent?.intent_detected || "Unknown",
      confidence: lastBotWithIntent?.confidence_score
        ? Math.round(lastBotWithIntent.confidence_score * 100)
        : 0,

      sentiment: lastBotWithIntent?.sentiment || "Neutral",

      unread: true,
      handledBy: session.handled_by || "chatbot",
      status: session.status === "ACTIVE" ? "Pending" : session.status,

      messages: msgs.map(m => ({
        type:
          m.sender === "user"
            ? "user"
            : m.sender === "bot"
              ? "bot"
              : "agent",

        text: m.message_text,
        timestamp: parseUTCDate(m.created_at)?.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        }) || "",

        intent: m.intent_label || m.intent_detected,
        confidence: m.confidence_score
          ? Math.round(m.confidence_score * 100)
          : null,

        sentiment: m.sentiment,
        entities: []
      }))
    };
  };



  // Apply the free-text search across name / email / session / message BEFORE
  // paginating, so a match on any page is found (not just the visible page).
  const searchLower = search.toLowerCase();
  const searchedConversations = filteredConversations.filter(conv =>
    !searchLower ||
    conv.user.toLowerCase().includes(searchLower) ||
    conv.email.toLowerCase().includes(searchLower) ||
    conv.id.toLowerCase().includes(searchLower) ||
    conv.firstMessage.toLowerCase().includes(searchLower)
  );

  const totalPages = Math.ceil(searchedConversations.length / itemsPerPage);

  // Status options come from the real data actually loaded, so every option
  // matches (no dead/hardcoded values that never filter anything).
  const statusOptions = [...new Set(conversations.map((c) => c.status).filter(Boolean))]
    .sort()
    .map((s) => ({ value: s, label: s }));


  const handleApplyFilters = () => {

    let filtered = [...conversations];

    if (startDate) {
      filtered = filtered.filter(
        (c) => new Date(c.startDate) >= new Date(startDate)
      );
    }

    if (endDate) {
      filtered = filtered.filter(
        (c) => c.endDate && new Date(c.endDate) <= new Date(endDate)
      );
    }

    if (statusFilter.length > 0) {
      const selectedStatus = statusFilter.map((s) => s.value);
      filtered = filtered.filter((c) =>
        selectedStatus.includes(c.status)
      );
    }

    setFilteredConversations(filtered);
    setCurrentPage(1);
  };



  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setStatusFilter([]);
    setSearch("");
    setCurrentPage(1);
    // Reset also reloads fresh data (replaces the separate Refresh button).
    fetchConversations();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const maxVisible = 3;
    let start = Math.max(currentPage - 1, 1);
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxVisible + 1, 1);
    }

    return Array.from(
      { length: end - start + 1 },
      (_, i) => start + i
    );
  };


  const exportJSON = () => {
    if (!selectedConversation) return;

    const json = JSON.stringify(selectedConversation, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `conversation-${selectedConversation.id}.json`;
    link.click();

    URL.revokeObjectURL(url);

  };

  const exportPDF = async () => {
    if (!selectedConversation) return;
    try {
      setToast({ variant: "info", msg: "Generating PDF…" });
      const { baseURL } = await import("../../APICalls/APICall");
      const res = await fetch(
        `${baseURL}/export/conversations/pdf?session_id=${selectedConversation.sessionId}&limit=500`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      if (!res.ok) throw new Error("PDF export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversation-${selectedConversation.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setToast({ variant: "success", msg: "PDF downloaded." });
    } catch {
      setToast({ variant: "danger", msg: "Failed to export PDF. Please try again." });
    }
  };

  const flagConversation = () => {
    setToast({ variant: "warning", msg: "Conversation flagged." });
  };

  const deleteConversation = () => {
    if (window.confirm("Are you sure you want to delete this conversation?")) {
      setToast({ variant: "success", msg: "Conversation deleted." });
    }
  };

  const escalateConversation = () => {
    setToast({ variant: "info", msg: "Conversation escalated to agent." });
  };


  const handleEscalate = async (row) => {
    if (!window.confirm("Escalate this conversation to a human agent?")) return;

    try {
      // FIX: postWT (no auth) → postT (Bearer token required by backend)
      await APICall.postT("/conversation/escalation", {
        session_id: row.sessionId,
        conversation_id: row.convoId,
        priority: "HIGH",
        reason: "Manual escalation from dashboard",
      });

      setToast({ variant: "success", msg: "Escalated successfully." });
      fetchConversations();
    } catch (err) {
      setToast({ variant: "danger", msg: "Failed to escalate. Please try again." });
    }
  };


  const paginatedConversations = searchedConversations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatChatText = (text) => {
    if (!text) return "";

    const html = text
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/^<strong>(.+?):<\/strong>$/gm, "<div class='chat-heading'>$1:</div>")
      .replace(/^[?•]\s*(.+)$/gm, "<div class='chat-bullet'>• $1</div>")
      .replace(/\n{2,}/g, "<div class='chat-gap'></div>")
      .replace(/\n/g, "<br />");
    return DOMPurify.sanitize(html);
  };

  return (

    <Card className="shadow-sm border-0 rounded-4 ">
      <Card.Body>
        {toast && (
          <Alert variant={toast.variant} dismissible onClose={() => setToast(null)} className="mb-3">
            {toast.msg}
          </Alert>
        )}
        <Row className="g-3 mb-3 align-items-end">
          <Col xs={12} md={6} lg={3}>
            <div className="cm-panel rounded-3 shadow-sm">
              <InputGroup>
                <InputGroup.Text className="border-0" style={{ background: "transparent", color: "var(--cvq-text-muted)" }}>
                  <Search size={12} />
                </InputGroup.Text>
                <Form.Control
                  className="cm-filter-input border-0 shadow-none"
                  placeholder="Search by name, email, message or session ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearch("")}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </div>
          </Col>

          <Col xs={12} md={6} lg={3}>
            <div className="cm-panel rounded-3 shadow-sm">
              <Select
                isMulti
                className="w-100"
                placeholder="Select Status"
                options={statusOptions}
                value={statusFilter}
                onChange={(selected) => setStatusFilter(selected || [])}
                styles={selectStyles}
              />
            </div>
          </Col>

          <Col xs={12} lg={6}>
            <div className="d-flex flex-wrap align-items-end gap-2 justify-content-lg-end">
              <div style={{ minWidth: 150 }}>
                <label className="cm-field-label">From</label>
                <div className="cm-panel rounded-3 shadow-sm">
                  <Form.Control
                    className="cm-filter-input border-0 shadow-none"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ minWidth: 150 }}>
                <label className="cm-field-label">To</label>
                <div className="cm-panel rounded-3 shadow-sm">
                  <Form.Control
                    className="cm-filter-input border-0 shadow-none"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <Button
                style={{ backgroundColor: "#e8710a", borderColor: "#e8710a", height: "40px" }}
                onClick={handleApplyFilters}>
                Apply
              </Button>
              <Button
                className="d-inline-flex align-items-center gap-2"
                style={{ backgroundColor: "#cbd5e1", borderColor: "#cbd5e1", color: "#0f172a", height: "40px" }}
                onClick={resetFilters}>
                <RefreshCcwIcon size={15} /> Reset
              </Button>
            </div>
          </Col>

          <Col xs={12} className="mt-3">
            <div className="cm-panel rounded-3 shadow-sm p-2" style={{ overflowX: "auto" }}>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <Table hover responsive className="align-middle mb-0 cm-conv-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>S.No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Last Message</th>
                      <th>Intent</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th style={{ width: 140 }} className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedConversations.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center cm-empty py-5">No conversations found.</td>
                      </tr>
                    ) : paginatedConversations.map((conv, idx) => (
                      <tr key={conv.id} style={{ cursor: "pointer" }} onClick={() => openChat(conv)}>
                        <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{ width: 30, height: 30, backgroundColor: "#e8710a", color: "#fff", fontWeight: "bold", fontSize: "0.75rem", flexShrink: 0 }}
                            >
                              {conv.avatar}
                            </span>
                            <span className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>{conv.user}</span>
                            {conv.isGuest && (
                              <span className="badge" style={{ backgroundColor: "var(--cvq-gray-200)", color: "var(--cvq-text-muted)", fontWeight: 500 }}>Guest</span>
                            )}
                            <HandledByBadge value={conv.handledBy} />
                          </div>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 200 }}>
                          {conv.email || <span className="text-muted">—</span>}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 240 }}>{conv.firstMessage}</td>
                        <td>
                          <span className="badge" style={{ backgroundColor: "rgba(232,113,10,0.18)", color: "var(--cvq-text)" }}>
                            {conv.intent}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: conv.status === "Resolved" ? "rgba(34,197,94,0.15)" :
                              conv.status === "Escalated" ? "rgba(239,68,68,0.15)" : "rgba(232,113,10,0.12)",
                            color: conv.status === "Resolved" ? "#22c55e" :
                              conv.status === "Escalated" ? "#ef4444" : "var(--cvq-text-muted)"
                          }}>
                            {conv.status}
                          </span>
                        </td>
                        <td className="text-nowrap"><small>{conv.lastTimestamp}</small></td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-inline-flex align-items-center gap-1"
                            title="View chat history"
                            onClick={() => openChat(conv)}
                          >
                            <Eye size={14} /> View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-2 mt-2">
                <PageSizeSelect
                  value={itemsPerPage}
                  onChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                />
                {totalPages > 1 && (
                  <div className="pagination-bar">
                    <button
                      className="text-nowrap p-2"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Prev
                    </button>

                    {getVisiblePages().map((page) => (
                      <button
                        key={page}
                        className={currentPage === page ? "active" : ""}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className="text-nowrap p-2"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Col>

        </Row>

        {/* Chat history modal — opens when a row / View is clicked */}
        <Modal show={showChatModal} onHide={() => setShowChatModal(false)} size="lg" centered>
          {selectedConversation && (
            <>
              <Modal.Header closeButton>
                <div>
                  <h6 className="mb-1">
                    {selectedConversation.user}
                    {selectedConversation.isGuest && (
                      <span className="badge ms-2" style={{ backgroundColor: "var(--cvq-gray-200)", color: "var(--cvq-text-muted)", fontWeight: 500 }}>
                        Guest
                      </span>
                    )}
                  </h6>
                  {selectedConversation.email && (
                    <div><small>✉ {selectedConversation.email}</small></div>
                  )}
                  <small className="text-muted">
                    Session: {selectedConversation.id} | Last Active: {selectedConversation.lastTimestamp}
                  </small>
                </div>
              </Modal.Header>

              <Modal.Body className="cm-chat-body px-3 py-3" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {selectedConversation.messages.length === 0 ? (
                  <div className="text-center cm-empty py-5">No messages in this conversation.</div>
                ) : selectedConversation.messages.map((msg, idx) => (
                  <div
                    key={`${msg.timestamp}-${msg.type}-${idx}`}
                    className={`d-flex mb-3 ${msg.type === "user" ? "justify-content-end" : "justify-content-start"}`}
                  >
                    <div className={`cm-bubble cm-bubble-${msg.type}`}>
                      <div
                        className="mb-1 chat-text"
                        dangerouslySetInnerHTML={{ __html: formatChatText(msg.text) }}
                      />

                      {msg.type === "user" && msg.intent && (
                        <small className="d-block cm-bubble-meta" style={{ opacity: 0.75 }}>
                          Intent: {msg.intent} | Confidence: {msg.confidence}%
                        </small>
                      )}

                      <small className="d-block cm-bubble-meta">
                        {msg.timestamp}
                      </small>
                    </div>
                  </div>
                ))}
              </Modal.Body>

              <Modal.Footer className="justify-content-between flex-wrap gap-2">
                <div className="small text-muted">
                  Sentiment: {selectedConversation.sentiment} | Confidence: {selectedConversation.confidence}% | Intent: {selectedConversation.intent} | Status: {selectedConversation.status}
                </div>
                <div className="d-flex gap-2">
                  <button className="cm-icon-btn" title="Export PDF" onClick={exportPDF}>
                    <FileText size={18} />
                  </button>
                  <button className="cm-icon-btn" title="Export JSON" onClick={exportJSON}>
                    <FileJson size={18} />
                  </button>
                  <Button
                    size="sm"
                    variant="warning"
                    disabled={
                      selectedConversation.status === "PENDING" ||
                      selectedConversation.status === "ESCALATED"
                    }
                    onClick={async () => {
                      if (!window.confirm("Escalate this conversation to a human agent?")) return;
                      await handleEscalate(selectedConversation);
                    }}
                  >
                    Escalate
                  </Button>
                </div>
              </Modal.Footer>
            </>
          )}
        </Modal>
      </Card.Body>
    </Card >



  );
};


export default ConversationManager;
